/**
 * Pantalla del **Historial de pedidos recibidos** (`/herramientas/pedidos-laminas/
 * historial`). Reemplaza el `PlaceholderPage` (no existía ruta React previa).
 * Equivale a `historial_pedidos_laminas.html` del SSR (demolido en esta feature).
 *
 * Tabla de solo lectura de los pedidos recibidos de la sede activa (`GET /pedidos-
 * laminas/historial`): fecha de recepción, material, descripción, cantidad, sedes,
 * quién recibió y fecha de creación. Exige `view_historial_pedidos_laminas` vía
 * `RequirePedidosPermission`. Datos de servidor con TanStack Query
 * (`usePedidosHistorial`); presentacional, sin `fetch`.
 */
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";
import { ROUTES } from "@/features/navigation";

import { VIEW_HISTORIAL_PERMISSION } from "../constants";
import { formatDate, referenciasDePedido } from "../format";
import { usePedidosHistorial } from "../hooks/usePedidosBoard";
import { RequirePedidosPermission } from "./RequirePedidosPermission";

function HistorialView() {
  const { data, isPending, isError, error } = usePedidosHistorial();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">
          Historial de pedidos recibidos
        </h1>
        <Button asChild variant="secondary" size="sm">
          <Link to={ROUTES.pedidosLaminas}>Volver a pedidos</Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isPending ? (
            <p className="px-1 py-8 text-center text-sm text-slate-500" role="status">
              Cargando historial…
            </p>
          ) : isError ? (
            <p className="px-1 py-8 text-center text-sm text-red-600" role="alert">
              {error instanceof Error && error.message
                ? error.message
                : "No se pudo cargar el historial."}
            </p>
          ) : data.recibidos.length === 0 ? (
            <p className="px-1 py-12 text-center text-sm text-slate-400">
              No hay pedidos recibidos en el historial.
            </p>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Fecha recepción</TableHeaderCell>
                  <TableHeaderCell>Material</TableHeaderCell>
                  <TableHeaderCell>Cantidad</TableHeaderCell>
                  <TableHeaderCell>Origen</TableHeaderCell>
                  <TableHeaderCell>Destino</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recibidos.map((pedido) => {
                  const refs = referenciasDePedido(pedido);
                  return (
                    <TableRow key={pedido.id}>
                      <TableCell className="font-medium text-slate-800">
                        {formatDate(pedido.fecha_recepcion)}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-slate-800">
                          {pedido.categoria}
                        </span>
                        {refs.length > 0 && (
                          <div className="text-xs text-slate-500">{refs.join(" · ")}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold text-slate-800">
                        {pedido.cantidad}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {pedido.origen_sede?.nombre ?? "—"}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {pedido.destino_sede?.nombre ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Pantalla del historial con el gating de permiso `view_historial_pedidos_laminas`. */
export function HistorialPedidosScreen() {
  return (
    <RequirePedidosPermission
      permission={VIEW_HISTORIAL_PERMISSION}
      title="Historial de pedidos recibidos"
      deniedMessage="No tienes permiso para ver el historial de pedidos."
    >
      <HistorialView />
    </RequirePedidosPermission>
  );
}
